import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import MeetingRoomUsage from '@/components/communication/MeetingRoomUsage';
import { fetchData } from '@/components/communication/MeetingRoomUsage/query-request';
import PeopleCounts from '@/components/communication/PeopleCounts';
import {
  getPeopleCountsAreaByFloorId,
  fetchPeopleCountFormat,
  fetchPeopleCountThresholds,
} from '@/components/communication/PeopleCounts/query-request';
import AirQualityNow from '@/components/facility_management/AirQuality/AirQualityNow';
import {
  fetchSensorDataNow,
  fetchSensorDataThreshold,
} from '@/components/facility_management/AirQuality/AirQualityNow/query-request';
import {
  COMPONENT_CHILD_ID_GROUP,
  COMPONENT_ID_GROUP,
} from '@/helpers/constants';
import {
  calculateRectangle,
  transformCoordinateBottomLeftToTopLeft,
  generateRandomString,
} from '@/helpers/utility';
import '@egjs/flicking-plugins/dist/arrow.css';
import Flicking, { ViewportSlot } from '@egjs/react-flicking';
import '@egjs/react-flicking/dist/flicking.css';
import { format } from 'date-fns';
import { isEqual, isNil } from 'lodash';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import React, { Component, createRef, Fragment } from 'react';
import { connect } from 'react-redux';
import Countdown from '@/components/CustomComponent/CustomCountDown/CustomCountDown'; // Import the Countdown component
import { COMMON_TEXT } from '@/helpers/common-text';
import CustomCurrentTime from '@/components/CustomComponent/CustomCurrentTime';

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class SignagePlay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFlickingPlay: false,
      fetchedData: {},
      isFetchingAllData: false,
      buttonsDisabled: false,
      countdownDuration: 0,
      countdownKey: generateRandomString(10),
      isPeopleFormatCount: false,
      areaLegend: [],
      peopleCountThresholds: null,
    };
    this.flickingDurationArr = [];
    this.autoplayTimeout = null;
    this.countdownTimeout = null;
    this.initialAutoplayTimeout = null;
    this.autoplayRestartTimeout = null;
    this.autoplayRetryTimeout = null;
    this.buttonDisableTimeout = null;
    this.isMountedSignage = false;
    this.autoplayGeneration = 0;
    this.bulkFetchGeneration = 0;
    this.rowFetchGenerationByRowId = {};
    this.flicking = createRef();
  }

  componentDidMount() {
    const { items } = this.props;
    this.isMountedSignage = true;
    this.flickingDurationArr = items.map(item => item.interval * 1000);
    this.getAllAndUpdateAllData({ items });
  }

  componentDidUpdate(prevProps, prevState) { }

  componentWillUnmount() {
    this.isMountedSignage = false;
    this.autoplayGeneration += 1;
    this.bulkFetchGeneration += 1;
    this.rowFetchGenerationByRowId = {};
    this.clearAllTimers();
  }

  safeSetState = (stateOrUpdater, callback) => {
    if (!this.isMountedSignage) {
      return;
    }
    this.setState(stateOrUpdater, callback);
  };

  clearTimeoutByName = timeoutName => {
    if (this[timeoutName]) {
      clearTimeout(this[timeoutName]);
      this[timeoutName] = null;
    }
  };

  setTimeoutByName = (timeoutName, callback, delay) => {
    this.clearTimeoutByName(timeoutName);
    this[timeoutName] = setTimeout(() => {
      this[timeoutName] = null;
      callback();
    }, delay);
  };

  clearAutoplayTimers = () => {
    [
      'autoplayTimeout',
      'countdownTimeout',
      'autoplayRestartTimeout',
      'autoplayRetryTimeout',
    ].forEach(timeoutName => this.clearTimeoutByName(timeoutName));
  };

  clearAllTimers = () => {
    this.clearAutoplayTimers();
    this.clearTimeoutByName('initialAutoplayTimeout');
    this.clearTimeoutByName('buttonDisableTimeout');
  };

  getDataRoomUsageByFloor = async ({ floorId }) => {
    let dataFloorMap = await fetchData({
      floorId: floorId,
    });
    if (!dataFloorMap) {
      dataFloorMap = [];
    }

    dataFloorMap = dataFloorMap
      .sort((a, b) => {
        if (a?.device_name < b?.device_name) {
          return -1;
        }
        if (a?.device_name > b?.device_name) {
          return 1;
        }
        return 0;
      })
      .map(item => ({
        ...item,
        timestamp: format(new Date(item.timestamp), 'MM月dd日 - HH:mm'),
      }));

    const updatedFloorMapData = dataFloorMap.map(item => ({
      position: {
        x: parseFloat(item?.position_x),
        y: parseFloat(item?.position_y),
      },
      data: item ?? null,
    }));

    return { updatedFloorMapData, dataFloorMap };
  };

  getDataPeopleCountsByFloor = async ({ floorId }) => {
    let dataFloorMap = await getPeopleCountsAreaByFloorId({
      floorId: floorId,
    });

    if (!dataFloorMap) {
      dataFloorMap = [];
    }

    const dataPeopleCounts = dataFloorMap.map(item => {
      const { position_x, position_y, position_x1, position_y1 } = item;
      const { pointC, width, height } = calculateRectangle({
        positionX: position_x,
        positionY: position_y,
        positionX1: position_x1,
        positionY1: position_y1,
      });
      const { x, y } = transformCoordinateBottomLeftToTopLeft({
        x: pointC.x,
        y: pointC.y,
      });
      item.rect = {
        top: y,
        left: x,
        width,
        height,
      };
      return item;
    });

    return { dataPeopleCounts };
  };

  getSensorDataByFloor = async ({ floorId }) => {
    let sensorDataRes = await fetchSensorDataNow({
      floorId: floorId,
    });

    let sensorData = [];
    if (sensorDataRes && sensorDataRes.length > 0) {
      sensorData = sensorDataRes.map(item => ({
        position: {
          x: parseFloat(item?.position_x),
          y: parseFloat(item?.position_y),
        },
        data: item ?? null,
        // For testing
        // data: {
        //   ...item ?? null,
        //   carbon_dioxide: Math.floor((Math.random() * 1000) + 1)
        // },
      }));
    }

    let sensorDataThreshold = await fetchSensorDataThreshold({
      floorId: floorId,
    });
    sensorDataThreshold = sensorDataThreshold?.[0] ?? {};
    return { sensorData, sensorDataThreshold };
  };

  getAllAndUpdateAllData = async ({ items }) => {
    const fetchGeneration = this.bulkFetchGeneration + 1;
    this.bulkFetchGeneration = fetchGeneration;
    this.safeSetState({ isFetchingAllData: true });
    const newFetchedData = {};
    const fetchPromises = items.map(
      async ({ component_id, floor_id, rowId }) => {
        try {
          const { data } = await this.getDataByComponentAndFloor({
            componentId: component_id,
            floorId: floor_id,
          });
          newFetchedData[rowId] = data;
        } catch (error) {
          console.error('Signage data fetch failed:', error);
          newFetchedData[rowId] = [];
        }
      }
    );
    await Promise.all(fetchPromises);

    if (!this.isMountedSignage || fetchGeneration !== this.bulkFetchGeneration) {
      return;
    }

    const { fetchedData } = this.state;
    const isDataChanged = !isEqual(fetchedData, newFetchedData);

    this.safeSetState(
      {
        fetchedData: isDataChanged ? newFetchedData : fetchedData,
        isFetchingAllData: false,
        isFlickingPlay: false,
      },
      () => {
        this.fetchDataByTenantId({ fetchGeneration });
        this.setTimeoutByName('initialAutoplayTimeout', () => {
          if (!this.isMountedSignage || fetchGeneration !== this.bulkFetchGeneration) {
            return;
          }
          this.safeSetState({ isFlickingPlay: true }, () => {
            this.startAutoplay();
          });
        }, 1000);
      }
    );
  };

  fetchDataByTenantId = async ({ fetchGeneration }) => {
    const { querystring } = this.props;
    const tenant = querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];

    let isPeopleFormatCount = false;
    let areaLegend = [];
    let dataPeopleCountFormat = [];
    let peopleCountThresholds = [];

    try {
      dataPeopleCountFormat = await fetchPeopleCountFormat({
        tenantId: tenant,
      });
      peopleCountThresholds = await fetchPeopleCountThresholds({
        tenantId: tenant,
      });
    } catch (error) {
      console.error('Signage tenant data fetch failed:', error);
    }

    if (!this.isMountedSignage || fetchGeneration !== this.bulkFetchGeneration) {
      return;
    }

    if (
      dataPeopleCountFormat &&
      dataPeopleCountFormat[0]?.people_counts_format === 'count'
    ) {
      isPeopleFormatCount = true;
    }

    let peopleCountThresholdsData = null;
    if (peopleCountThresholds && peopleCountThresholds[0]) {
      const { good, fair, bad, poor } = peopleCountThresholds[0];
      peopleCountThresholdsData = { good, fair, bad, poor };
      
      // Use % symbol only when format is percentage, not count
      const percentSymbol = isPeopleFormatCount ? '' : '%';
      
      areaLegend = [
        {
          label: COMMON_TEXT.EMPTY,
          color: '#00B573',
          value: `~ ${good - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#ADDE61',
          value: `${good}${percentSymbol} ~ ${fair - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#FFE135',
          value: `${fair}${percentSymbol} ~ ${bad - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#FFA14F',
          value: `${bad}${percentSymbol} ~ ${poor - 1}${percentSymbol}`,
        },
        {
          label: COMMON_TEXT.CROWDED,
          color: '#E16A7C',
          value: `${poor}${percentSymbol} ~`,
        },
      ];
    }

    this.safeSetState({
      isPeopleFormatCount,
      areaLegend,
      peopleCountThresholds: peopleCountThresholdsData,
    });
  };

  getAndUpdateData = async ({ componentId, floorId, rowId, itemInterval }) => {
    const requestGeneration = (this.rowFetchGenerationByRowId[rowId] || 0) + 1;
    this.rowFetchGenerationByRowId[rowId] = requestGeneration;

    let data = [];
    try {
      const result = await this.getDataByComponentAndFloor({
        componentId,
        floorId,
      });
      data = result.data;
    } catch (error) {
      console.error('Signage slide data fetch failed:', error);
      return;
    }

    if (
      !this.isMountedSignage ||
      this.rowFetchGenerationByRowId[rowId] !== requestGeneration
    ) {
      return;
    }

    this.safeSetState(prevState => {
      const currentDataByRowId = prevState.fetchedData[rowId] ?? [];
      if (isEqual(currentDataByRowId, data)) {
        return null;
      }
      return {
        fetchedData: {
          ...prevState.fetchedData,
          [rowId]: data,
        },
      };
    });
  };

  getDataByComponentAndFloor = async ({ componentId, floorId }) => {
    let data = [];
    switch (componentId) {
      case COMPONENT_ID_GROUP.ROOM_AVAILABILITIES:
        data = await this.getDataRoomUsageByFloor({ floorId: floorId });
        break;
      case COMPONENT_ID_GROUP.PEOPLE_COUNTS:
        data = await this.getDataPeopleCountsByFloor({ floorId: floorId });
        break;
      case COMPONENT_ID_GROUP.ENVIRONMENTS:
        data = await this.getSensorDataByFloor({ floorId: floorId });
        break;
      default:
        break;
    }
    return { data: data ?? [] };
  };

  // Flicking event listener
  flickingOnChanged = ({ index }) => {
    const { isFlickingPlay } = this.state;
    if (!this.isMountedSignage || !isFlickingPlay) {
      return;
    }

    const { items } = this.props;
    if (!items || items.length === 0) {
      return;
    }

    const nextSlideIndex = (index + 1) % items.length;
    const nextSlideItem = items[nextSlideIndex];
    const { component_id, floor_id, rowId, interval } = nextSlideItem;
    this.getAndUpdateData({
      componentId: component_id,
      floorId: floor_id,
      rowId,
      itemInterval: interval,
    });

    this.clearTimeoutByName('autoplayTimeout');
    this.setTimeoutByName('autoplayRestartTimeout', () => {
      if (this.isMountedSignage && this.state.isFlickingPlay) {
        this.startAutoplay();
      }
    }, 100);
  };

  getCurrentFlickingIndex = () => {
    return this.flicking.current?.index ?? 0;
  };

  isAutoplayGenerationActive = generation => {
    return (
      this.isMountedSignage &&
      generation === this.autoplayGeneration &&
      this.state.isFlickingPlay
    );
  };

  startAutoplay = () => {
    const { items } = this.props;
    const { isFlickingPlay } = this.state;

    this.clearAutoplayTimers();

    if (!this.isMountedSignage || !isFlickingPlay || !items || items.length === 0 || !this.flicking.current) {
      return;
    }

    const generation = this.autoplayGeneration + 1;
    this.autoplayGeneration = generation;
    const currentIndex = this.getCurrentFlickingIndex();
    const currentItem = items[currentIndex];
    const interval = currentItem?.interval || 5;
    const countdownStartTime = Math.max(0, (interval - 3) * 1000);

    if (interval >= 3) {
      this.setTimeoutByName('countdownTimeout', () => {
        if (this.isAutoplayGenerationActive(generation)) {
          this.safeSetState({
            countdownDuration: 3000,
            countdownKey: generateRandomString(10),
          });
        }
      }, countdownStartTime);
    }

    this.setTimeoutByName('autoplayTimeout', () => {
      if (!this.isAutoplayGenerationActive(generation) || !this.flicking.current) {
        return;
      }

      this.safeSetState({ countdownDuration: 0 });

      if (!this.flicking.current.animating) {
        const nextIndex = (currentIndex + 1) % items.length;
        this.flicking.current.moveTo(nextIndex)
          .then(() => {
            if (this.isAutoplayGenerationActive(generation)) {
              this.startAutoplay();
            }
          })
          .catch(error => {
            console.warn('Autoplay moveTo failed:', error);
            if (this.isAutoplayGenerationActive(generation)) {
              this.setTimeoutByName('autoplayRetryTimeout', () => {
                if (this.isAutoplayGenerationActive(generation)) {
                  this.startAutoplay();
                }
              }, 1000);
            }
          });
      } else if (this.isAutoplayGenerationActive(generation)) {
        this.setTimeoutByName('autoplayRetryTimeout', () => {
          if (this.isAutoplayGenerationActive(generation)) {
            this.startAutoplay();
          }
        }, 500);
      }
    }, interval * 1000);
  };

  // flicking button control
  flickingButtonGroup = () => {
    const { isFlickingPlay, buttonsDisabled, countdownDuration, countdownKey } = this.state;

    const pauseTheFlicking = () => {
      if (isFlickingPlay) {
        this.autoplayGeneration += 1;
        this.clearAutoplayTimers();
        this.safeSetState({
          isFlickingPlay: false,
          countdownDuration: 0,
        });
      }
    };

    const disableButtonsTemporarily = () => {
      this.safeSetState({ buttonsDisabled: true });
      this.setTimeoutByName('buttonDisableTimeout', () => {
        this.safeSetState({ buttonsDisabled: false });
      }, 700);
    };

    const playBtnOnClicked = async () => {
      if (isFlickingPlay) {
        pauseTheFlicking();
      } else {
        this.safeSetState({ isFlickingPlay: true }, () => {
          this.startAutoplay();
        });
      }
      disableButtonsTemporarily();
    };

    const nextBtnOnClicked = async () => {
      pauseTheFlicking();

      const flicking = this.flicking.current;
      if (!flicking || !flicking.panelCount) {
        disableButtonsTemporarily();
        return;
      }

      const currentIndex = this.getCurrentFlickingIndex();
      const newIndex = (currentIndex + 1) % flicking.panelCount;
      if (!flicking.animating) {
        flicking
          .moveTo(newIndex)
          .catch(error => {
            console.warn('Manual navigation failed:', error);
          });
      }
      disableButtonsTemporarily();
    };

    const prevBtnOnClicked = async () => {
      pauseTheFlicking();

      const flicking = this.flicking.current;
      if (!flicking || !flicking.panelCount) {
        disableButtonsTemporarily();
        return;
      }

      const currentIndex = this.getCurrentFlickingIndex();
      const totalCount = flicking.panelCount;
      const newIndex = (currentIndex - 1 + totalCount) % totalCount;
      if (!flicking.animating) {
        flicking
          .moveTo(newIndex)
          .catch(error => {
            console.warn('Manual navigation failed:', error);
          });
      }
      disableButtonsTemporarily();
    };

    return (
      <div className="signage-footer">
        <div className="control-button-container">
          <Button
            text
            className="control-button control-back-btn"
            icon="pi pi-backward"
            severity="secondary"
            disabled={buttonsDisabled}
            onClick={() => prevBtnOnClicked()}
          ></Button>
          <Button
            text
            className="control-button control-play-btn"
            icon={`pi pi-${isFlickingPlay ? 'pause' : 'play'}`}
            severity="secondary"
            disabled={buttonsDisabled}
            onClick={() => playBtnOnClicked()}
          ></Button>
          <Button
            text
            className="control-button control-next-btn"
            icon="pi pi-forward"
            severity="secondary"
            disabled={buttonsDisabled}
            onClick={() => nextBtnOnClicked()}
          ></Button>
        </div>
        <Countdown duration={countdownDuration} key={countdownKey} />
      </div>
    );
  };

  renderComponent = ({
    item,
    fetchedData,
    index,
    isPeopleFormatCount,
    areaLegend,
    peopleCountThresholds,
  }) => {
    const { component_id, floor_id, component_child_id, rowId } = item;
    const componentData = fetchedData[rowId];
    const signageModeData = {
      data: componentData ?? [],
      floor: floor_id,
    };
    if (isNil(componentData)) {
      return <Fragment key={-1}></Fragment>;
    }
    switch (component_id) {
      case COMPONENT_ID_GROUP.ROOM_AVAILABILITIES:
        return (
          <MeetingRoomUsage
            key={index}
            floor={floor_id}
            isSignageMode={true}
            signageModeData={signageModeData}
            CurrentTimeComponent={CustomCurrentTime}
          />
        );
      case COMPONENT_ID_GROUP.PEOPLE_COUNTS:
        return (
          <PeopleCounts
            key={index}
            signageModeData={signageModeData}
            isSignageMode={true}
            isPeopleFormatCount={isPeopleFormatCount}
            areaLegend={areaLegend}
            peopleCountThresholds={peopleCountThresholds}
            CurrentTimeComponent={CustomCurrentTime}
          />
        );
      case COMPONENT_ID_GROUP.ENVIRONMENTS:
        switch (component_child_id) {
          case COMPONENT_CHILD_ID_GROUP.TEMPERATURE:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'temperature',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          case COMPONENT_CHILD_ID_GROUP.HUMIDITY:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'humidity',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          case COMPONENT_CHILD_ID_GROUP.CARBON_DIOXIDE:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'carbon_dioxide',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          case COMPONENT_CHILD_ID_GROUP.NOISE_LEVEL:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'noise_level',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          default:
            return <Fragment key={-2}></Fragment>;
        }
      default:
        break;
    }
    return <Fragment key={-3}></Fragment>;
  };

  flickingComponent = () => {
    const { items } = this.props;
    const { fetchedData, isPeopleFormatCount, areaLegend, peopleCountThresholds } = this.state;

    const { notice } = this.props;
    const noticeStr = notice.replace(/\\n/g, '\n');

    return (
      <Flicking
        ref={this.flicking}
        panelsPerView={1}
        duration={1000}
        align={'center'}
        moveType={['strict', { count: 1 }]}
        preventClickOnDrag={true}
        preventDefaultOnDrag={true}
        changeOnHold={true}
        renderOnlyVisible={true}
        circular={true}
        onChanged={this.flickingOnChanged}
        autoResize={true}
        useResizeObserver={true}
      >
        {items.map((item, idx) => (
          <div className="panel" key={idx}>
            {this.renderComponent({
              index: idx,
              item,
              fetchedData,
              isPeopleFormatCount,
              areaLegend,
              peopleCountThresholds,
            })}
          </div>
        ))}
        <ViewportSlot>
          {noticeStr && (
            <div className="notice">
              <div className="notice-content">
                <div className="notice-icon"></div>
                <div className="notice-text">{noticeStr}</div>
                {/* Hidden for compatibility with CSS */}
                <div className="title-text" style={{ display: 'none' }}>{COMMON_TEXT.TITLE_NOTICE}</div>
                <span className="config-content" style={{ display: 'none' }}>{noticeStr}</span>
              </div>
            </div>
          )}
          {this.flickingButtonGroup()}
        </ViewportSlot>
      </Flicking>
    );
  };

  render() {
    const { isFetchingAllData } = this.state;
    const FlickingMemo = this.flickingComponent;

    return (
      <div className="signage-play-container">
        <div className="card">
          {isFetchingAllData ? (
            <LoadingSpinner />
          ) : (
            <>
              <FlickingMemo />
            </>
          )}
        </div>
      </div>
    );
  }
}

SignagePlay.propTypes = {
  items: PropTypes.array,
  notice: PropTypes.string,
};
export default SignagePlay;
